// frontend/app/components/lessonPlans/authoring/lessonDraft.js
//
// The authoring draft: how one is created, validated, and turned into the API
// payload. Pure functions with no React, because this is the part where a
// mistake silently produces a lesson that looks fine and fails at runtime.

import { validateBlock } from "./blockSchemas";
import { PHASE_META, SECTION_META } from "../lessonPlanTheme";

/**
 * 2 = Saras Learning Loop vocabulary. Every lesson the wizard writes is a v2
 * lesson, including one that was loaded as v1: the runtime normalizes a v1
 * lesson's phases into Loop vocabulary on read, so the steps this draft holds
 * are already `wonder`/`understand`/…. Saving them back against
 * `schema_version: 1` would send them through the v1 crosswalk a second time
 * and silently rename every phase — legacy `explain` and Loop `explain` are
 * different steps.
 */
export const LOOP_SCHEMA_VERSION = 2;

/**
 * Per-phase starting points for a new lesson. Modes are the document's own
 * gradual release; minutes add up to a 40-minute period. A new lesson's four
 * steps take the mode of their section's first phase and the minutes of all
 * the section's phases (see `defaultLoopSteps`).
 *
 * The phase *list* is never written down: it is `PHASE_META`, so the day a
 * phase is added or renamed this map degrades to a default instead of
 * disagreeing with the rail.
 */
const LOOP_STEP_DEFAULTS = {
  wonder: { mode: "I_DO", minutes: 4 },
  explore: { mode: "YOU_DO", minutes: 8 },
  understand: { mode: "I_DO", minutes: 7 },
  apply: { mode: "YOU_DO", minutes: 8 },
  explain: { mode: "YOU_DO", minutes: 6 },
  reflect: { mode: "YOU_DO", minutes: 4 },
  grow: { mode: "WE_DO", minutes: 3 },
};

const FALLBACK_STEP_DEFAULTS = { mode: "WE_DO", minutes: "" };

/** The phases a lesson can use, in the order the rail plays them. */
export const loopPhaseKeys = () => Object.keys(PHASE_META);

/** The first phase, for "add a step" when the author has not chosen one. */
export const firstPhaseKey = () => loopPhaseKeys()[0] || "";

/**
 * A new lesson's skeleton: exactly four steps, one per section, in order. Each
 * step carries its section's first phase as its hidden key (the Loop's seven
 * phases still carry mastery evidence), is titled with the section's name, and
 * starts empty. Mode comes from that first phase; minutes add up the section's
 * phases, so the four steps still make a 40-minute period. An author can
 * delete, duplicate or reorder them, but starts from the whole lesson frame
 * rather than a blank page.
 */
export const defaultLoopSteps = (t) => {
  const translate = t || ((value) => value);
  return Object.values(SECTION_META).map((section) => {
    const phase = section.phases[0];
    const defaults = LOOP_STEP_DEFAULTS[phase] || FALLBACK_STEP_DEFAULTS;
    const minutes = section.phases.reduce(
      (sum, key) => sum + (LOOP_STEP_DEFAULTS[key]?.minutes || 0),
      0
    );
    return {
      step_key: phase,
      phase,
      instructional_mode: defaults.mode,
      title: translate(section.label),
      subtitle: "",
      teacher_guidance: "",
      student_activity: "",
      estimated_minutes: String(minutes || defaults.minutes || ""),
      blocks: [],
      completion_rules: [],
    };
  });
};

export const slugify = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 200);

export const emptyDraft = (board) => ({
  slug: "",
  title: "",
  subtitle: "",
  description: "",
  base_board: board || "",
  // UI-only, for the anchor picker. Stripped before the payload is sent.
  level_id: null,
  subject_id: null,
  subject_name: "",
  chapter_id: null,
  chapter_name: "",
  board_topic_id: null,
  source_topic_id: null,
  topic_name: "",
  lesson_no: "1",
  duration_minutes: "",
  // S3: the wizard no longer surfaces a Difficulty control (slide 4 — reads as
  // "three versions of the same lesson", which our 37 lessons are not). The
  // column stays, and toApiPayload still maps "" to null, so a lesson that
  // never sets one simply has none rather than inheriting "Core" by accident.
  difficulty: "",
  objectives: [""],
  prerequisites: [""],
  materials: [""],
  teacher_tips: [],
  resource_shelf: {},
  status: "draft",
  visibility_scope: "public",
});

/** Turn a runtime payload back into an editable draft. */
export const draftFromLesson = (lesson) => {
  if (!lesson) return emptyDraft("");
  return {
    ...emptyDraft(lesson.base_board),
    slug: lesson.slug || "",
    title: lesson.title || "",
    subtitle: lesson.subtitle || "",
    description: lesson.description || "",
    base_board: lesson.base_board || "",
    subject_name: lesson.subject_name || "",
    chapter_id: lesson.chapter_id ?? null,
    chapter_name: lesson.chapter_name || "",
    board_topic_id: lesson.board_topic_id ?? null,
    source_topic_id: lesson.source_topic_id ?? null,
    topic_name: lesson.topic_name || "",
    lesson_no: String(lesson.lesson_no ?? 1),
    duration_minutes:
      lesson.duration_minutes === null || lesson.duration_minutes === undefined
        ? ""
        : String(lesson.duration_minutes),
    difficulty: lesson.difficulty || "",
    objectives: lesson.objectives?.length ? [...lesson.objectives] : [""],
    prerequisites: lesson.prerequisites?.length ? [...lesson.prerequisites] : [""],
    materials: lesson.materials?.length ? [...lesson.materials] : [""],
    teacher_tips: lesson.teacher_tips ? [...lesson.teacher_tips] : [],
    resource_shelf: lesson.resource_shelf || {},
    status: lesson.status || "draft",
    visibility_scope: lesson.visibility_scope || "public",
  };
};

export const stepsFromLesson = (lesson) =>
  (lesson?.steps || []).map((step) => ({
    step_key: step.step_key,
    phase: step.phase,
    instructional_mode: step.instructional_mode || null,
    title: step.title || "",
    subtitle: step.subtitle || "",
    teacher_guidance: step.teacher_guidance || "",
    student_activity: step.student_activity || "",
    estimated_minutes:
      step.estimated_minutes === null || step.estimated_minutes === undefined
        ? ""
        : String(step.estimated_minutes),
    // Resolution metadata is derived on read; keeping it would send stale
    // resolved names back to the server as if they were authored.
    blocks: (step.blocks || []).map(
      ({ resolution_status, resolution_error, resolved_asset, ...block }) => block
    ),
    completion_rules: step.completion_rules || [],
  }));

export const conceptsFromLesson = (lesson) =>
  (lesson?.concepts || []).map((concept) => ({
    concept_key: concept.concept_key,
    label: concept.label,
    objective_index: concept.objective_index ?? null,
  }));

const cleanList = (items) =>
  (items || []).map((item) => String(item || "").trim()).filter(Boolean);

const toIntOrNull = (value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isInteger(parsed) ? parsed : null;
};

/**
 * Everything wrong with the draft, keyed for the step that owns it.
 * `{ details: {...}, concepts: {...}, phases: {...}, blocking: bool }`
 */
export const validateDraft = ({ draft, concepts = [], steps = [], t }) => {
  const details = {};
  const conceptErrors = {};
  const phases = {};

  if (!String(draft.title || "").trim()) details.title = "A title is required";
  if (!String(draft.slug || "").trim()) details.slug = "A web address is required";
  else if (slugify(draft.slug) !== draft.slug) {
    details.slug = "Use lowercase letters, numbers and dashes only";
  }

  if (!draft.board_topic_id) {
    details.board_topic_id = "Pick the topic this lesson belongs to";
  }
  if (toIntOrNull(draft.lesson_no) === null) {
    details.lesson_no = "Lesson number must be a whole number";
  }
  if (String(draft.duration_minutes ?? "").trim() && toIntOrNull(draft.duration_minutes) === null) {
    details.duration_minutes = "Duration must be a whole number of minutes";
  }
  if (cleanList(draft.objectives).length === 0) {
    details.objectives = "At least one objective is required";
  }

  const conceptKeys = concepts.map((c) => c.concept_key);
  if (new Set(conceptKeys).size !== conceptKeys.length) {
    conceptErrors.concepts = "Two concepts share the same key";
  }
  if (concepts.some((c) => !String(c.concept_key || "").trim())) {
    conceptErrors.concepts = "Every concept needs a key";
  }

  if (steps.length === 0) {
    phases.steps = "A lesson needs at least one step";
  }
  const stepKeys = steps.map((s) => s.step_key);
  if (new Set(stepKeys).size !== stepKeys.length) {
    phases.steps = "Two steps share the same key";
  }
  if (steps.some((s) => !String(s.title || "").trim())) {
    phases.steps = "Every step needs a title";
  }

  // Block-level problems roll up here so Review can show a count without
  // re-deriving them, and so publishing cannot bypass them.
  const blockProblems = [];
  steps.forEach((step) => {
    (step.blocks || []).forEach((block) => {
      validateBlock(block, { conceptKeys, t }).forEach((problem) => {
        blockProblems.push({
          step_key: step.step_key,
          step_title: step.title,
          block_id: block.id,
          block_type: block.type,
          problem,
        });
      });
    });
  });

  return {
    details,
    concepts: conceptErrors,
    phases,
    blockProblems,
    blocking:
      Object.keys(details).length > 0 ||
      Object.keys(conceptErrors).length > 0 ||
      Object.keys(phases).length > 0 ||
      blockProblems.length > 0,
  };
};

/**
 * Authored lists lose their blank rows, and anything the runtime reads as
 * "omitted" is written as null rather than "". A reflection whose
 * still-confusing box was cleared must arrive as null, not as an empty string
 * the renderer would draw as a captionless box.
 */
const cleanBlock = (block) => {
  const cleaned = { ...block };

  if (Array.isArray(cleaned.items) && typeof cleaned.items[0] === "string") {
    cleaned.items = cleanList(cleaned.items);
  }
  if (Array.isArray(cleaned.questions)) cleaned.questions = cleanList(cleaned.questions);
  if (Array.isArray(cleaned.rubric)) cleaned.rubric = cleanList(cleaned.rubric);

  if (Array.isArray(cleaned.prompts)) {
    cleaned.prompts = cleaned.prompts
      .filter((prompt) => String(prompt?.label || "").trim())
      .map((prompt) => ({
        key: String(prompt.key || "").trim(),
        label: String(prompt.label).trim(),
      }));
  }
  if (cleaned.type === "reflection") {
    cleaned.confusion_prompt = String(cleaned.confusion_prompt ?? "").trim() || null;
  }
  if (cleaned.min_words !== undefined) {
    cleaned.min_words = toIntOrNull(cleaned.min_words);
  }

  return cleaned;
};

/** The POST/PATCH body. UI-only fields are dropped here, not at the call site. */
export const toApiPayload = ({ draft, concepts = [], steps = [], ownerEmail }) => ({
  slug: slugify(draft.slug),
  title: String(draft.title || "").trim(),
  subtitle: String(draft.subtitle || "").trim() || null,
  description: String(draft.description || "").trim() || null,

  base_board: draft.base_board,
  subject_name: draft.subject_name || null,
  chapter_id: draft.chapter_id ?? null,
  chapter_name: draft.chapter_name || null,
  board_topic_id: draft.board_topic_id ?? null,
  source_topic_id: draft.source_topic_id ?? null,
  topic_name: draft.topic_name || null,

  lesson_no: toIntOrNull(draft.lesson_no) ?? 1,
  duration_minutes: toIntOrNull(draft.duration_minutes),
  difficulty: String(draft.difficulty || "").trim() || null,

  objectives: cleanList(draft.objectives),
  prerequisites: cleanList(draft.prerequisites),
  materials: cleanList(draft.materials),
  teacher_tips: cleanList(draft.teacher_tips),
  resource_shelf: draft.resource_shelf || {},

  owner_email: ownerEmail || null,
  visibility_scope: draft.visibility_scope || "public",
  status: draft.status || "draft",
  schema_version: LOOP_SCHEMA_VERSION,

  concepts: concepts
    .filter((c) => String(c.concept_key || "").trim())
    .map((concept, index) => ({
      concept_key: concept.concept_key.trim(),
      label: String(concept.label || concept.concept_key).trim(),
      objective_index: concept.objective_index ?? null,
      sequence: index,
    })),

  // `sequence` is assigned from array order, so what the author sees in the
  // list is what the runtime plays.
  steps: steps.map((step, index) => ({
    step_key: step.step_key,
    sequence: index + 1,
    phase: step.phase,
    instructional_mode: step.instructional_mode || null,
    title: String(step.title || "").trim(),
    subtitle: String(step.subtitle || "").trim() || null,
    teacher_guidance: String(step.teacher_guidance || "").trim() || null,
    student_activity: String(step.student_activity || "").trim() || null,
    estimated_minutes: toIntOrNull(step.estimated_minutes),
    blocks: (step.blocks || []).map(cleanBlock),
    completion_rules: step.completion_rules || [],
  })),
});
